import { axios_nomination } from '@/plugins/axios';

import configuration from '@/configurations';

class UserRepository {
  /**
   * @param {Number} user_id
   * @returns {Promise}
   */
  async getUserById(user_id) {
    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/users/${user_id}`);

    return result.data;
  }

  /**
   * @param {Number} id_list
   * @returns {Promise}
   */
  async search(criteria) {
    const {
      organization_id_list,
      status_id_list,
      id_list,
      limit,
      offset,
      sort_list,
    } = criteria;

    const params = {
      organization_id_list,
      status_id_list,
      id_list,
      limit,
      offset,
      sort_list,
    };
    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/users`,
        {
          params,
        });

    return result.data;
  }

  /**
   * @param {Number} organization_id
   *
   * @returns {Promise}
   */
  async getOrganizationById(organization_id) {
    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/organizations/${organization_id}`);

    return result.data;
  }

  /**
   *
   * @returns {Promise}
   */
  async getUsersGlobalConfigurations() {
    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/users/global-configurations`);

    return result.data;
  }

  async getUserGlobalNews(query_id_list) {
    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/users/global-news`, {
        params: {
          query_id_list,
        },
      });

    return result.data;
  }

  /**
   *
   * @param {Number} user_id
   * @param {String[]} name_list
   * @returns
   */
  async getUserPropertyList(user_id, name_list) {
    const result = await axios_nomination
      .get(`${configuration.api_host}/v2/users/properties`, {
        params: {
          name_list,
          user_id_list: [user_id],
        },
      });

    return result.data;
  }

  /**
   *
   * @param {Object} data
   * @param {Number} data.user_id
   * @param {String} data.name
   * @param {String} data.value
   * @returns
   */
  async createUserProperty(data) {
    const result = await axios_nomination
      .post(
        `${configuration.api_host}/v2/users/properties`,
        data
      );

    return result.data;
  }

  /**
   *
   * @param {Number} id
   * @param {Object} data
   * @param {String} data.value
   */
  async updateUserProperty(id, data) {
    const result = await axios_nomination
      .patch(
        `${configuration.api_host}/v2/users/properties/${id}`,
        data
      );

    return result.data;
  }

  async deleteUserProperty(id) {
    const result = await axios_nomination
      .delete(`${configuration.api_host}/v2/users/properties/${id}`);

    return result.data;
  }

  async getUserFormationList(user_id) {
    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/users/${user_id}/formations`);

    return result.data;
  }

  async createFormation(user_id, data) {
    const result = await axios_nomination
      .post(
        `${configuration.api_host}/v1/users/${user_id}/formations`,
        data
      );

    return result.data;
  }

  async updateFormation(user_id, formation_id, data) {
    const result = await axios_nomination
      .put(
        `${configuration.api_host}/v1/users/${user_id}/formations/${formation_id}`,
        data
      );

    return result.data;
  }

  async deleteFormation(user_id, formation_id) {
    const result = await axios_nomination
      .delete(`${configuration.api_host}/v1/users/${user_id}/formations/${formation_id}`);

    return result.data;
  }

  async getUserExperienceList(user_id) {
    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/users/${user_id}/experiences`);

    if (result.data.error !== undefined) {
      console.error(result.data.error);

      result.data = {
        user_experience_list: [],
      };
    }

    return result.data;
  }

  async createExperience(user_id, data) {
    const result = await axios_nomination
      .post(
        `${configuration.api_host}/v1/users/${user_id}/experiences`,
        data
      );

    return result.data;
  }

  async updateExperience(user_id, experience_id, data) {
    const result = await axios_nomination
      .put(
        `${configuration.api_host}/v1/users/${user_id}/experiences/${experience_id}`,
        data
      );

    return result.data;
  }

  async deleteExperience(user_id, experience_id) {
    const result = await axios_nomination
      .delete(`${configuration.api_host}/v1/users/${user_id}/experiences/${experience_id}`);

    return result.data;
  }

  getUserDataLocalStorage() {
    return localStorage.user_data
      ? JSON.parse(localStorage.user_data)
      : null;
  }

  setUserDataLocalStorage(user_data) {
    localStorage.user_data = user_data;
  }

  removeUserDataLocalStorage() {
    localStorage.removeItem('user_data');
  }

  async updateUser(user_id, input) {
    const result = await axios_nomination
      .patch(`${configuration.api_host}/v1/users/${user_id}`,
        input
      );

    return result.data;
  }

  async getIntercomVerifiedHash() {
    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/users/intercom-verified-hash`);

    return result.data.user_hash;
  }

  async createSenderUser(input) {
    const {
      civility_id,
      custom_subscription_end_date,
      custom_subscription_start_date,
      email,
      first_name,
      last_name,
      license_id,
      organization_id,
    } = input;

    const result = await axios_nomination
      .post(
        `${configuration.api_host}/v1/users/senders`,
        {
          civility: {
            id: civility_id,
          },
          custom_subscription_end_date,
          custom_subscription_start_date,
          email: email,
          first_name: first_name,
          last_name: last_name,
          license: {
            id: license_id,
          },
          organization: {
            id: organization_id,
          },
        }
      );

    return result.data;
  }

  async updateAutonomousUser(user_id, input) {
    const {
      first_name,
      last_name,
    } = input;

    const result = await axios_nomination
      .patch(
        `${configuration.api_host}/v1/users/autonomous-users/${user_id}`,
        {
          first_name,
          last_name,
        }
      );

    return result.data;
  }

  async getOrganizationStatistics(input) {
    const {
      date_lower_boundary,
      date_upper_boundary,
      group_by,
      organization_id,
      operation_name,
      statistic_type_list,
      user_id_list,
    } = input;

    const params = {
      date_lower_boundary,
      date_upper_boundary,
      statistic_type_list,
      user_id_list,
    };

    if (group_by) {
      params.group_by = group_by;
    }

    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/organizations/${organization_id}/statistics/${operation_name}`,
        {
          params,
        }
      );

    return result.data;
  }

  async getStatisticsPerUser(input) {
    const {
      date_lower_boundary,
      date_upper_boundary,
      group_by,
      organization_id,
      operation_name,
      statistic_type_list,
      user_id_list,
    } = input;

    const params = {
      date_lower_boundary,
      date_upper_boundary,
      statistic_type_list,
      user_id_list,
    };

    if (group_by) {
      params.group_by = group_by;
    }

    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/organizations/${organization_id}/users/statistics/${operation_name}`,
        {
          params,
        }
      );

    return result.data;
  }

  async getManagedUsers(user_id, query) {
    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/users/${user_id}/managed-users`, {
        params: query,
      });

    return result.data;
  }

  async getUserHistory(user_id, input) {
    const result = await axios_nomination
      .get(`${configuration.api_host}/v1/users/${user_id}/histories`, {
        params: input,
      });

    return result.data;
  }
}

const user_repository = new UserRepository();

export {
  user_repository,
};
